import { useMemo, useState } from "react";
import dayjs from "dayjs";
import useSWR from "swr";
import { ColumnsType } from "antd/es/table";
import { Search } from "lucide-react"
import { Input } from "../../../components/ui/input"
import { Link, useParams, } from "react-router-dom"
import placeholder from "../../../util/placeholder"
import { Skeleton, Table, Tabs, TabsProps } from "antd"
import BreadCrumb from "../../../componentsV2/BreadCrumb"
import { REQUEST_STATUS } from "../../../util/constants";
import { useCurrency } from "../../../hooks/useCurrency";
import { fetcher } from "../../../util/axiosConfig";
import VehicleDetails from "../../../componentsV2/VehicleDetails";

export default function TruckRequests() {
  const params =  useParams();
  const [status, setStatus] = useState('Assigned');
  const [searchQuery, setSearchQuery] = useState("")
  const {formatCurrency} = useCurrency();

  const { data: requestsData, isLoading: isLoadingRequests } = useSWR(`newAdmin/vehicles/${params?.truckId}/renter-requests/?status=${status}`, fetcher);

  const { data, isLoading } = useSWR(`newAdmin/vehicles/${params?.truckId}`, fetcher);

  const vehicle: Vehicle = useMemo(() => {
    return data?.data?.vehicleDetails ?? null;
  }, [data]);

  const requests: Request[] = useMemo(() => {
    if (!requestsData?.data?.renterRequests) return [];
    const assignments = requestsData?.data?.renterRequests ?? [];
    let filteredRequests: Request[] = [];

    if (assignments.length) {
      filteredRequests = assignments.map((item: any) => ({
        id: item.renterRequestAssignment.renterRequest?.id,
        reference: item.renterRequestAssignment.renterRequest.referenceId,
        value: item.renterRequestAssignment.renterRequest?.totalAmount,
        date: item.renterRequestAssignment.renterRequest?.createdAt,
        status: item.renterRequestAssignment.renterRequest?.status,
        trips: item.noOfTrips,
        material: item.renterRequestAssignment.renterRequest?.material,
        vehicle: item.renterRequestAssignment?.vehicle,
        renter: {
          name: item.renterRequestAssignment.renterRequest?.renterName,
          phone: item.renterRequestAssignment.renterRequest?.renterPhone
        },
      }));
    }

    if (status) {
      filteredRequests = assignments
        .filter((item: any) => item.renterRequestAssignment.status === status)
        .map((item: any) => ({
          id: item.renterRequestAssignment.renterRequest?.id,
          reference: item.renterRequestAssignment.renterRequest.referenceId,
          value: item.renterRequestAssignment.renterRequest?.totalAmount,
          date: item.renterRequestAssignment.renterRequest?.createdAt,
          status: item.renterRequestAssignment.renterRequest?.status,
          trips: item.noOfTrips,
          material: item.renterRequestAssignment.renterRequest?.material,
          vehicle: item.renterRequestAssignment?.vehicle,
          renter: {
            name: item.renterRequestAssignment.renterRequest?.renterName,
            phone: item.renterRequestAssignment.renterRequest?.renterPhone
          },
      }));
    }
  
    if (searchQuery) {
      filteredRequests = filteredRequests.filter((request) => request.reference.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    return filteredRequests;
  }, [requestsData, status, searchQuery]);

  const columns: ColumnsType<Request> = [
    {
      title: "Reference",
      dataIndex: "reference",
      key: "reference",
      render: (_, record, index) => (
        <Link to={`/admin/trucks/transaction/${record?.id}/?vehicleId=${params?.truckId}`}>{record?.reference || '1234'}</Link>
      ),
    },
    {
      title: "Value",
      dataIndex: "value",
      key: "value",
      render: (_, record, index) => (
        <span className="text-secondary">
          {formatCurrency(record?.value)}
        </span>
      )
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      render: (_, record, index) => (
        <span>
          {dayjs(record?.date).format('DD/MM/YYYY')}
        </span>
      ),
    },
    {
      title: "Trips",
      dataIndex: "trips",
      key: "trips",
      render: (_, record, index) => (
        <span className="text-primary">
          {record?.trips}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, record, index) => (
        <span className="text-primary font-medium !text-sm">
          {record?.status}
        </span>
      ),
    },
    {
      title: "Renter",
      dataIndex: "renter",
      key: "renter",
      render: (_, record, index) => (
        <Link to="#">{record?.renter?.name}</Link>
      ),
    },
    {
      title: "Material",
      dataIndex: "material",
      key: "material",
      render: (_, record, index) => (
        <div className="relative w-[100px] h-[46px] bg-gray-100 rounded overflow-hidden">
            <img
              src={record?.material?.image || placeholder}
              alt={record?.material?.name}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
              <span className="text-white font-medium capitalize">{record?.material?.name}</span>
            </div>
        </div>
      ),
    },
  ]

  const items: TabsProps["items"] = [
    {
      key: 'Assigned',
      label: "Pending",
      children: isLoadingRequests ? <Skeleton active paragraph={{ rows: 8 }} /> : <Table columns={columns} dataSource={requests} rowKey="id" />,
    },
    {
      key: 'Accepted',
      label: "Active",
      children: isLoadingRequests ? <Skeleton active paragraph={{ rows: 8 }} /> : <Table columns={columns} dataSource={requests} rowKey="id" />,
    },
    {
      key: 'Completed',
      label: "Completed",
      children: isLoadingRequests ? <Skeleton active paragraph={{ rows: 8 }} /> : <Table columns={columns} dataSource={requests} rowKey="id" />,
    }
  ];

  const SearchVehicles = (<div className="relative w-full sm:w-auto">
    <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
    <Input
      placeholder="Search"
      className="pl-8 w-full sm:w-[300px]"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
    />
  </div>);

  return (
    <main className="min-h-screen bg-primary-accent p-6 h-full">
        <div className="h-full flex flex-col lg:flex-row gap-6">
            {/* Left section - Rental Request list */}
            <div className="flex-1 bg-white  h-full rounded-lg shadow-sm border p-6">
                {/* Back button and title */}
                <BreadCrumb label={`${vehicle?.referenceId ?? 'Vehicle'} - Rentals`} showBack />
                {/* Tabs and search */}
                <div className="overflow-x-auto">
                  <Tabs
                    defaultActiveKey={REQUEST_STATUS.ACTIVE}
                    items={items}
                    tabBarExtraContent={SearchVehicles}
                    onChange={(key) => setStatus(key)}
                  />
                </div>
            </div>
            {/* Right section - Vehicle Details */}
            <VehicleDetails vehicle={vehicle} />
        </div>
    </main>
  )
}
